import { supabase } from '../lib/supabase';
import { normalizeExperienceLevelToUi, getExperienceLevelLabelAr } from '../utils/experienceLevel';
import { calculateDistance } from './locationService';
import { resolveEffectiveWorkloadMultiplier } from '../utils/workloadPricing';
import { pricingService } from './pricingService';
import { getPhotographyTypeLabelAr } from '../utils/bookingLabels';

// Convert snake_case to camelCase
export const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// ─── City / category lookups (maybeSingle — avoids PostgREST 406 on zero rows) ─
const CITY_KEY_TO_AR = {
  riyadh: 'الرياض',
  jeddah: 'جدة',
  dammam: 'الدمام',
  mecca: 'مكة المكرمة',
  medina: 'المدينة المنورة',
  khobar: 'الخبر',
  taif: 'الطائف',
  tabuk: 'تبوك',
  abha: 'أبها',
  buraidah: 'بريدة',
};

const CATEGORY_TYPE_TO_AR = {
  wedding: 'تصوير مناسبات',
  event: 'تصوير مناسبات',
  portrait: 'تصوير أشخاص',
  commercial: 'تصوير منتجات',
  'real-estate': 'تصوير عقارات',
  product: 'تصوير منتجات',
  food: 'تصوير منتجات',
  fashion: 'تصوير أشخاص',
  sports: 'تصوير أشخاص',
  تصوير: 'تصوير مناسبات',
};

async function fetchActiveCityNames() {
  const { data, error } = await supabase
    .from('cities')
    .select('name_ar')
    .eq('is_active', true)
    .order('name_ar');
  if (error) {
    console.warn('[bookingService] fetchActiveCityNames:', error.message);
    return [];
  }
  return (data || []).map((c) => c.name_ar);
}

async function fetchActiveCategoryNames() {
  const { data, error } = await supabase
    .from('categories')
    .select('name_ar')
    .eq('is_active', true)
    .order('name_ar');
  if (error) {
    console.warn('[bookingService] fetchActiveCategoryNames:', error.message);
    return [];
  }
  return (data || []).map((c) => c.name_ar);
}

/** @returns {Promise<{ cityId: string, cityNameAr: string }>} */
async function resolveCityId(cityInput) {
  const raw = (cityInput || 'الرياض').toString().trim();
  const cityNameAr = CITY_KEY_TO_AR[raw.toLowerCase()] || raw;

  const { data: city, error } = await supabase
    .from('cities')
    .select('id')
    .eq('name_ar', cityNameAr)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.warn('[resolveCityId] query error:', error.message, error.code);
  }
  if (city?.id) {
    return { cityId: city.id, cityNameAr };
  }

  const available = await fetchActiveCityNames();
  throw new Error(
    `المدينة «${cityNameAr}» غير متوفرة في النظام حالياً. المدن المتاحة: ${available.join('، ') || 'لا توجد مدن مفعّلة'}.`,
  );
}

/** @returns {Promise<{ categoryId: string, categoryNameAr: string }>} */
async function resolveCategoryId(photoTypeInput) {
  const photoType = (photoTypeInput || 'تصوير').toString().trim();
  const categoryNameAr = CATEGORY_TYPE_TO_AR[photoType] || photoType;

  const { data: category, error } = await supabase
    .from('categories')
    .select('id')
    .eq('name_ar', categoryNameAr)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.warn('[resolveCategoryId] query error:', error.message, error.code);
  }
  if (category?.id) {
    return { categoryId: category.id, categoryNameAr };
  }

  const available = await fetchActiveCategoryNames();
  throw new Error(
    `نوع التصوير «${categoryNameAr}» غير متوفر في النظام حالياً. الأنواع المتاحة: ${available.join('، ') || 'لا توجد فئات مفعّلة'}.`,
  );
}

function buildBookingAnswers(bookingData) {
  return {
    photographyType: bookingData.photographyType || bookingData.serviceType || null,
    city: bookingData.city || null,
    region: bookingData.region || null,
    duration: bookingData.duration || null,
    experienceLevel: bookingData.experienceLevel || null,
    equipment: bookingData.equipment || [],
    shootingMethod: bookingData.shootingMethod || null,
    shootingPlaceType: bookingData.shootingPlaceType || null,
    deliverableMode: bookingData.deliverableMode || null,
    photosCount: bookingData.photosCount ?? null,
    videosCount: bookingData.videosCount ?? null,
    shortVideosCount: bookingData.shortVideosCount ?? null,
    notes: bookingData.notes || bookingData.customerNotes || null,
  };
}

// ─── Non-blocking email helper ────────────────────────────────────────────────
// Fetches user emails from user_profiles then calls the send-email Edge Function.
// Never throws — failures are logged silently so they don't break booking flow.
const PLACEHOLDER_EMAIL_SUFFIX = '@placeholder.auth';

async function sendBookingEmail({ userIds = [], templateKey, templateData = {} }) {
  try {
    if (!userIds.length) return;

    // Resolve emails from user_profiles
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, email, role')
      .in('id', userIds);

    if (!profiles?.length) return;

    // For each user, collect real emails (skip phone-based placeholder addresses)
    const realEmailMap = {};
    for (const p of profiles) {
      if (p.email && !p.email.endsWith(PLACEHOLDER_EMAIL_SUFFIX)) {
        realEmailMap[p.id] = p.email;
      }
    }

    // For photographers whose user_profiles.email is a placeholder,
    // fall back to photographer_profiles.contact_email
    const photographerIds = profiles
      .filter(p => p.role === 'photographer' && !realEmailMap[p.id])
      .map(p => p.id);

    if (photographerIds.length) {
      const { data: pgProfiles } = await supabase
        .from('photographer_profiles')
        .select('user_id, contact_email')
        .in('user_id', photographerIds);

      for (const pg of pgProfiles || []) {
        if (pg.contact_email && !pg.contact_email.endsWith(PLACEHOLDER_EMAIL_SUFFIX)) {
          realEmailMap[pg.user_id] = pg.contact_email;
        }
      }
    }

    const emails = Object.values(realEmailMap).filter(Boolean);
    if (!emails.length) {
      console.warn(`📧 Email (${templateKey}): no real email addresses found for userIds=${userIds.join(',')}`);
      return;
    }

    await supabase.functions.invoke('send-email', {
      body: { to: emails, template_key: templateKey, template_data: templateData },
    });
  } catch (err) {
    console.warn(`📧 Email (${templateKey}) failed (non-blocking):`, err?.message || err);
  }
}

// ─── Send email to all admin accounts (non-blocking) ─────────────────────────
export async function sendAdminEmail({ templateKey, templateData = {} }) {
  try {
    const { data: admins } = await supabase
      .from('user_profiles')
      .select('email')
      .eq('role', 'admin');

    const emails = (admins || [])
      .map(a => a.email)
      .filter(e => e && !e.endsWith(PLACEHOLDER_EMAIL_SUFFIX));

    if (!emails.length) return;

    await supabase.functions.invoke('send-email', {
      body: { to: emails, template_key: templateKey, template_data: templateData },
    });
  } catch (err) {
    console.warn(`📧 Admin email (${templateKey}) failed (non-blocking):`, err?.message || err);
  }
}

// ─── Photographer new-booking notifications (Push + WhatsApp + SMS + optional email) ──

function buildBookingScheduleLabels(booking) {
  const sched = booking?.scheduled_at ? new Date(booking.scheduled_at) : null;
  const dateStr = sched
    ? sched.toLocaleDateString('ar-SA-u-ca-gregory', { weekday: 'long', day: 'numeric', month: 'long' })
    : (booking?.booking_date || '—');
  const timeStr = sched
    ? sched.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })
    : (booking?.booking_time || '');
  const typeLabel =
    getPhotographyTypeLabelAr(booking?.photography_type) || 'جلسة تصوير';
  return { dateStr, timeStr, typeLabel };
}

async function sendPhotographerNewBookingEmail(photographerId, booking) {
  if (!photographerId || !booking?.id) return;

  const bookingShortId = String(booking.id).slice(0, 8).toUpperCase();
  const { dateStr, timeStr, typeLabel } = buildBookingScheduleLabels(booking);
  const amountStr = String(booking.final_price_sar ?? booking.price ?? '');

  const relatedIds = [photographerId, booking.customer_id].filter(Boolean);
  const { data: profiles } = relatedIds.length
    ? await supabase.from('user_profiles').select('id, full_name').in('id', relatedIds)
    : { data: [] };
  const nameById = Object.fromEntries((profiles || []).map((p) => [p.id, p.full_name || '']));

  await sendBookingEmail({
    userIds: [photographerId],
    templateKey: 'photographer_new_booking',
    templateData: {
      photographer_name: nameById[photographerId] || '',
      booking_id: bookingShortId,
      customer_name: nameById[booking.customer_id] || '',
      photography_type: typeLabel,
      booking_date: dateStr,
      booking_time: timeStr,
      booking_location: booking.location || booking.city || '',
      total_amount: amountStr,
    },
  });
}

/**
 * Same notification channels as the first assigned photographer on createBooking.
 * @param {boolean} options.includeEmail — true for reassignment (INSERT uses DB trigger)
 */
async function notifyPhotographerNewBookingChannels(
  photographerId,
  bookingId,
  bookingRow = null,
  { includeEmail = false } = {},
) {
  if (!photographerId || !bookingId) return;

  let booking = bookingRow;
  if (!booking?.id) {
    const { data } = await supabase.from('bookings').select('*').eq('id', bookingId).maybeSingle();
    booking = data;
  }
  if (!booking) {
    console.warn('notifyPhotographerNewBookingChannels: booking not found', bookingId);
    return;
  }

  try {
    const { notifyPhotographerNewBooking } = await import('./notificationService');
    await notifyPhotographerNewBooking(photographerId, bookingId);
  } catch (e) {
    console.warn('⚠️ Photographer new booking push failed (non-blocking):', e?.message || e);
  }

  try {
    const { whatsappService } = await import('./whatsappService');
    await whatsappService.notifyPhotographerNewBooking(photographerId, bookingId);
  } catch (e) {
    console.warn('⚠️ WhatsApp failed (optional):', e?.message || e);
  }

  try {
    const { data: phUser } = await supabase
      .from('user_profiles')
      .select('phone')
      .eq('id', photographerId)
      .maybeSingle();
    if (phUser?.phone) {
      const { dateStr, timeStr, typeLabel } = buildBookingScheduleLabels(booking);
      await sendSmsNonBlocking({
        phone: phUser.phone,
        message: `لديك حجز جديد (${typeLabel}) يوم: ${dateStr} ، الساعة: ${timeStr} . افتح تطبيق مشهد لقبول الحجز`,
      });
    }
  } catch (e) {
    console.warn('⚠️ SMS to photographer failed (non-blocking):', e?.message || e);
  }

  if (includeEmail) {
    try {
      await sendPhotographerNewBookingEmail(photographerId, booking);
    } catch (e) {
      console.warn('⚠️ Email to photographer failed (non-blocking):', e?.message || e);
    }
  }
}

// ─── Non-blocking SMS (Msegat via send-sms Edge Function) ───────────────────
async function sendSmsNonBlocking({ phone, phones, message }) {
  try {
    if (!message?.trim()) return;
    const body = phone ? { phone, message: message.trim() } : { phones, message: message.trim() };
    if (phone && !String(phone).replace(/\D/g, '').length) return;
    if (phones && !phones.length) return;

    const { data, error } = await supabase.functions.invoke('send-sms', { body });
    if (error) console.warn('📱 SMS invoke error (non-blocking):', error?.message || error);
    else if (data && data.ok === false) console.warn('📱 SMS send failed (non-blocking):', data?.message);
  } catch (e) {
    console.warn('📱 SMS failed (non-blocking):', e?.message || e);
  }
}

export const bookingService = {
  // Get all bookings for current user (customer or photographer)
  async getMyBookings(userRole) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // For photographers, check both assigned_photographer_id (for pending requests) and photographer_id (for confirmed bookings)
    let bookings = [];
    
    if (userRole === 'photographer') {
      // Photographers should see bookings assigned to them (pending) OR confirmed to them
      // Use separate queries and combine results for better compatibility
      const [pendingBookings, confirmedBookings] = await Promise.all([
        // Bookings assigned to photographer (pending confirmation)
        supabase
          .from('bookings')
          .select('*')
          .eq('assigned_photographer_id', user.id)
          .order('created_at', { ascending: false }),
        
        // Bookings confirmed to photographer
        supabase
          .from('bookings')
          .select('*')
          .eq('photographer_id', user.id)
          .neq('assigned_photographer_id', user.id) // Exclude duplicates
          .order('created_at', { ascending: false })
      ]);
      
      // Combine results and remove duplicates
      const allBookings = [
        ...(pendingBookings.data || []),
        ...(confirmedBookings.data || [])
      ];
      
      // Remove duplicates by ID
      const uniqueBookings = allBookings.filter((booking, index, self) =>
        index === self.findIndex((b) => b.id === booking.id)
      );
      
      bookings = uniqueBookings;
      
      console.log('📸 Photographer bookings loaded:', {
        photographerId: user.id,
        pendingCount: pendingBookings.data?.length || 0,
        confirmedCount: confirmedBookings.data?.length || 0,
        totalCount: bookings.length,
        pendingBookings: pendingBookings.data?.map(b => ({ id: b.id, status: b.status, assigned_photographer_id: b.assigned_photographer_id }))
      });
      
      if (pendingBookings.error) {
        console.error('❌ Error fetching pending bookings:', pendingBookings.error);
      }
      if (confirmedBookings.error) {
        console.error('❌ Error fetching confirmed bookings:', confirmedBookings.error);
      }
    } else {
      // Customers see their own bookings
      const { data, error } = await supabase
      .from('bookings')
      .select('*')
        .eq('customer_id', user.id)
      .order('created_at', { ascending: false });
      
      if (error) throw error;
      bookings = data || [];
    }

    if (!bookings || bookings.length === 0) {
      console.log('No bookings found for user:', { userRole, userId: user.id });
      return [];
    }

    // Then, get related user profiles and photographer_profiles (avatar from both for "حجوزاتي" list)
    const customerIds = [...new Set(bookings.map(b => b.customer_id).filter(Boolean))];
    const photographerIds = [...new Set(bookings.map(b => b.photographer_id || b.assigned_photographer_id || b.photographerId || b.assignedPhotographerId).filter(Boolean))];
    const allUserIds = [...new Set([...customerIds, ...photographerIds])];

    let userProfiles = {};
    let photographerProfilesByUserId = {};
    if (allUserIds.length > 0) {
      const [profilesResult, ppResult] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('id, full_name, email, phone, avatar_url')
          .in('id', allUserIds),
        photographerIds.length > 0
          ? supabase
              .from('photographer_profiles')
              .select('user_id, avatar_url, business_name')
              .in('user_id', photographerIds)
          : Promise.resolve({ data: null, error: null })
      ]);
      if (!profilesResult.error && profilesResult.data) {
        profilesResult.data.forEach(profile => {
          userProfiles[String(profile.id)] = profile;
        });
      }
      if (!ppResult.error && ppResult.data) {
        ppResult.data.forEach(pp => {
          photographerProfilesByUserId[String(pp.user_id)] = pp;
        });
      }
    }

    // Combine bookings: photographer from user_profiles (when allowed by RLS) or from photographer_profiles (اسم من business_name)
    const enrichedBookings = bookings.map(booking => {
      const photographerId = booking.photographer_id || booking.assigned_photographer_id || booking.photographerId || booking.assignedPhotographerId;
      const userProfile = photographerId ? (userProfiles[String(photographerId)] || userProfiles[photographerId] || null) : null;
      const ppProfile = photographerId ? (photographerProfilesByUserId[String(photographerId)] || photographerProfilesByUserId[photographerId] || null) : null;
      let photographer = null;
      if (userProfile) {
        photographer = {
          ...userProfile,
          avatar_url: userProfile.avatar_url || ppProfile?.avatar_url,
          avatarUrl: userProfile.avatar_url || ppProfile?.avatar_url,
          business_name: userProfile.business_name ?? ppProfile?.business_name,
          businessName: userProfile.business_name ?? ppProfile?.business_name
        };
      } else if (ppProfile) {
        const displayName = ppProfile.business_name || 'مصور';
        photographer = {
          id: photographerId,
          full_name: displayName,
          fullName: displayName,
          avatar_url: ppProfile.avatar_url || null,
          avatarUrl: ppProfile.avatar_url || null,
          business_name: ppProfile.business_name,
          businessName: ppProfile.business_name
        };
      } else if (photographerId) {
        photographer = { id: photographerId, full_name: 'مصور', fullName: 'مصور' };
      }
      return {
        ...booking,
        customer: userProfiles[String(booking.customer_id)] || userProfiles[booking.customer_id] || null,
        photographer
      };
    });

    return toCamelCase(enrichedBookings);
  },

  // Get bookings by status
  async getBookingsByStatus(status, userRole) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const column = userRole === 'photographer' ? 'photographer_id' : 'customer_id';
    
    // First, get bookings
    const { data: bookings, error } = await supabase
      .from('bookings')
      .select('*')
      .eq(column, user?.id)
      .eq('status', status)
      .order('created_at', { ascending: false });

    if (error) throw error;
    if (!bookings || bookings.length === 0) return [];

    // ثم جلب user_profiles للمصوّرين: في الحجز العادي قبل القبول يكون photographer_id فارغاً و assigned_photographer_id معبّأ
    const customerIds = [...new Set(bookings.map(b => b.customer_id).filter(Boolean))];
    const photographerIds = [...new Set(bookings.map(b => b.photographer_id || b.assigned_photographer_id || b.photographerId || b.assignedPhotographerId).filter(Boolean))];
    const allUserIds = [...new Set([...customerIds, ...photographerIds])];

    let userProfiles = {};
    if (allUserIds.length > 0) {
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, phone, avatar_url')
        .in('id', allUserIds);

      if (!profilesError && profiles) {
        profiles.forEach(profile => {
          userProfiles[profile.id] = profile;
        });
      }
    }

    // تحديد المصور للعرض: photographer_id إن وُجد (بعد القبول)، وإلا assigned_photographer_id (قبل القبول)
    const enrichedBookings = bookings.map(booking => {
      const photographerId = booking.photographer_id || booking.assigned_photographer_id || booking.photographerId || booking.assignedPhotographerId;
      const photographer = photographerId ? (userProfiles[String(photographerId)] || userProfiles[photographerId] || null) : null;
      return {
        ...booking,
        customer: userProfiles[booking.customer_id] || null,
        photographer
      };
    });

    return toCamelCase(enrichedBookings);
  },

  // Create new booking with photographer confirmation flow
  // Follows the PROMPT requirements: status = pending_photographer_confirmation, hold_expires_at = now + 10 minutes
  async createBooking(bookingData) {
    console.log('🟢🟢🟢 createBooking STARTED 🟢🟢🟢');
    console.log('📦 Step 0: Checking authentication...');
    
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      console.error('❌ Not authenticated');
      throw new Error('Not authenticated');
    }
    console.log('✅ User authenticated:', user.id);

    console.log('📦 Creating booking with data:', bookingData);

    // 1. Resolve category_id (maybeSingle — no 406 when zero rows)
    console.log('📦 Step 1: Getting category_id...');
    const photoType = (bookingData.photographyType || bookingData.serviceType || 'تصوير').toString();
    const { categoryId, categoryNameAr } = await resolveCategoryId(photoType);
    console.log('✅ Step 1 complete: categoryId =', categoryId, categoryNameAr);

    // 2. Resolve city_id (maybeSingle — no 406 when zero rows)
    console.log('📦 Step 2: Getting city_id...');
    const cityName = (bookingData.city || 'الرياض').toString();
    const { cityId, cityNameAr } = await resolveCityId(cityName);
    console.log('✅ Step 2 complete: cityId =', cityId, cityNameAr);

    // 3. Calculate scheduled_at from date and time (REQUIRED)
    console.log('📦 Step 3: Calculating scheduled_at...');
    let scheduledAt = null;
    if (bookingData.date || bookingData.bookingDate) {
      const dateStr = bookingData.date || bookingData.bookingDate;
      const timeStr = bookingData.time || bookingData.bookingTime || '10:00';
      // Parse time (handle both HH:MM and HH:MM:SS formats)
      const [hours, minutes] = timeStr.split(':');
      scheduledAt = `${dateStr}T${hours}:${minutes || '00'}:00+03:00`;
      
      console.log('📅 scheduled_at calculation:', {
        dateStr,
        timeStr,
        hours,
        minutes,
        scheduledAt,
        isValid: !isNaN(new Date(scheduledAt).getTime())
      });
    } else {
      // If no date provided, use tomorrow at 10:00 AM as default
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      scheduledAt = tomorrow.toISOString().split('T')[0] + 'T10:00:00+03:00';
      
      console.log('⚠️ No date provided, using default (tomorrow):', {
        tomorrow: tomorrow.toISOString().split('T')[0],
        scheduledAt
      });
    }

    // Validate scheduled_at is a valid date and in the future
    const scheduledDate = new Date(scheduledAt);
    if (!scheduledAt || isNaN(scheduledDate.getTime())) {
      throw new Error('تاريخ ووقت الحجز غير صالح. يرجى التحقق من البيانات.');
    }
    
    // Check if scheduled_at is in the past (allow same day bookings)
    const now = new Date();
    const timeDiff = scheduledDate.getTime() - now.getTime();
    if (timeDiff < -3600000) { // Allow 1 hour buffer for same-day bookings
      console.warn('⚠️ scheduled_at is in the past:', {
        scheduledAt,
        scheduledDate: scheduledDate.toISOString(),
        now: now.toISOString(),
        timeDiffHours: timeDiff / (1000 * 60 * 60)
      });
      // Don't throw error, just warn - allow admin to handle edge cases
    }

    // 4. Calculate final_price_sar (REQUIRED)
    // Try multiple sources for price
    let finalPrice = null;
    
    // Priority 1: finalPrice from bookingData
    if (bookingData.finalPrice) {
      finalPrice = parseFloat(bookingData.finalPrice);
    }
    // Priority 2: price from bookingData
    else if (bookingData.price) {
      finalPrice = parseFloat(bookingData.price);
    }
    // Priority 3: from calculatedPrice (if available)
    else if (bookingData.calculatedPrice?.totalPrice) {
      finalPrice = parseFloat(bookingData.calculatedPrice.totalPrice);
    }
    // Priority 4: from pricingSnapshot
    else if (bookingData.pricingSnapshot?.total) {
      finalPrice = parseFloat(bookingData.pricingSnapshot.total);
    }
    else {
      finalPrice = 0;
    }
    
    console.log('💰 final_price_sar calculation:', {
      sources: {
        bookingDataFinalPrice: bookingData.finalPrice,
        bookingDataPrice: bookingData.price,
        calculatedPriceTotalPrice: bookingData.calculatedPrice?.totalPrice,
        pricingSnapshotTotal: bookingData.pricingSnapshot?.total
      },
      calculatedFinalPrice: finalPrice,
      isValid: !isNaN(finalPrice) && finalPrice > 0,
      type: typeof finalPrice,
      rounded: Math.round(finalPrice * 100) / 100 // Round to 2 decimal places
    });
    
    if (isNaN(finalPrice) || finalPrice <= 0) {
      console.error('❌ Invalid final_price_sar:', {
        finalPrice,
        bookingData: {
          finalPrice: bookingData.finalPrice,
          price: bookingData.price,
          calculatedPrice: bookingData.calculatedPrice,
          pricingSnapshot: bookingData.pricingSnapshot
        }
      });
      throw new Error('السعر غير صالح. يرجى التحقق من السعر المحسوب.');
    }
    
    // Round to 2 decimal places for currency
    finalPrice = Math.round(finalPrice * 100) / 100;

    // 5. Build pricing snapshot (REQUIRED)
    const workloadBucket = bookingData.workloadBucket || null;
    const photographyTypeForPricing = bookingData.photographyType || bookingData.serviceType || '';
    const productPhysicalSizeBucket =
      photographyTypeForPricing === 'product' ? (bookingData.productPhysicalSizeBucket || null) : null;

    let pricingCfg = { workloadMultiplierByBucket: {}, productPhysicalMultiplierByBucket: {} };
    try {
      pricingCfg = await pricingService.getPricingConfig();
    } catch (e) {
      console.warn('⚠️ getPricingConfig for workload snapshot:', e?.message || e);
    }
    const workloadPricingMaps = {
      workloadByBucket: pricingCfg.workloadMultiplierByBucket || {},
      productPhysicalByBucket: pricingCfg.productPhysicalMultiplierByBucket || {},
    };
    const workloadMultiplier = resolveEffectiveWorkloadMultiplier(
      photographyTypeForPricing,
      workloadBucket || '',
      productPhysicalSizeBucket || '',
      workloadPricingMaps
    );

    // Always build a fresh pricing snapshot — never carry over stale cached prices
    // If checkout already built a full snapshot (v2.1), use it but override price/audit fields.
    // Non-price fields (breakdown, multipliers, deliverables) from checkout snapshot are preserved.
    const incomingSnap = bookingData.pricingSnapshot || {};
    const normalizedExpUi =
      normalizeExperienceLevelToUi(bookingData.experienceLevel || incomingSnap.experienceLevel) ||
      bookingData.experienceLevel ||
      incomingSnap.experienceLevel ||
      null;
    const normalizedExpLabel =
      bookingData.experienceLevelLabel ||
      incomingSnap.experienceLevelLabel ||
      (normalizedExpUi ? getExperienceLevelLabelAr(normalizedExpUi) : null);
    const deliverableMode = bookingData.deliverableMode || incomingSnap.deliverableMode || 'custom';
    const photosCount = deliverableMode !== 'unspecified' ? (bookingData.photosCount ?? incomingSnap.photosCount ?? 10) : null;
    const videosCount = deliverableMode !== 'unspecified' ? (bookingData.videosCount ?? incomingSnap.videosCount ?? 0) : null;
    const shortVideosCount = deliverableMode !== 'unspecified' ? (bookingData.shortVideosCount ?? incomingSnap.shortVideosCount ?? 0) : null;
    const deliverableLabel = bookingData.deliverableLabel || incomingSnap.deliverableLabel ||
      (deliverableMode === 'unspecified'
        ? 'غير محدد (يقدرها المصور)'
        : [
            photosCount > 0 ? `${photosCount} صورة` : null,
            videosCount > 0 ? `${videosCount} فيديو طويل` : null,
            shortVideosCount > 0 ? `${shortVideosCount} فيديو قصير` : null,
          ].filter(Boolean).join(' + ') || null);

    const pricingSnapshot = {
      // Preserve rich breakdown data from checkout snapshot when available
      ...incomingSnap,
      // Always override with current booking data (fresh, not cached)
      photographyCategory: bookingData.photographyCategory || incomingSnap.photographyCategory || null,
      photographyCategoryLabel: bookingData.photographyCategoryLabel || incomingSnap.photographyCategoryLabel || null,
      photographyType: bookingData.photographyType || incomingSnap.photographyType,
      city: bookingData.city || incomingSnap.city,
      locationType: bookingData.locationType || incomingSnap.locationType,
      duration: bookingData.duration || incomingSnap.duration,
      experienceLevel: normalizedExpUi || incomingSnap.experienceLevel,
      experienceLevelLabel: normalizedExpLabel,
      equipment: bookingData.equipment || incomingSnap.equipment || [],
      shootingMethod: bookingData.shootingMethod || null,
      shootingMethods: bookingData.shootingMethods || incomingSnap.shootingMethods || (bookingData.shootingMethod ? [bookingData.shootingMethod] : []),
      shootingPlaceType: bookingData.shootingPlaceType || incomingSnap.shootingPlaceType || null,
      workloadBucket: workloadBucket,
      workloadMultiplier: workloadMultiplier,
      productPhysicalSizeBucket: productPhysicalSizeBucket,
      productPhysicalSizeLabel: bookingData.productPhysicalSizeLabel || incomingSnap.productPhysicalSizeLabel || null,
      deliveredPhotosBucket: bookingData.deliveredPhotosBucket || null,
      // Deliverables — always from current wizard answers
      deliverableMode,
      photosCount,
      videosCount,
      shortVideosCount,
      deliverableLabel,
      // Price fields — always use the freshly validated finalPrice (never cached)
      basePrice: bookingData.basePrice || incomingSnap.baseHourPrice || null,
      vat: bookingData.vat || incomingSnap.vatAmount || null,
      vatAmount: bookingData.vat || incomingSnap.vatAmount || null,
      totalPrice: finalPrice,
      total: finalPrice,
      // Audit
      calculatedAt: new Date().toISOString(),
      version: '2.1',
    };

    // 6. Build location text
    let locationText = 'موقع';
    if (bookingData.shootingPlaceType === 'photographer_studio') {
      locationText = bookingData.photographerStudioLocationSnapshot?.address_text || 
                     bookingData.photographerStudioLocationSnapshot?.city || 
                     'استديو المصور';
    } else {
      locationText = bookingData.preciseLocation?.address_text 
        || bookingData.location 
        || `${bookingData.locationType || 'موقع'}، ${bookingData.city || 'الرياض'}`
        || 'موقع';
    }
    
    // 7. Set hold_expires_at = now + 3 minutes
    const holdExpiresAt = new Date(Date.now() + 3 * 60 * 1000).toISOString();

    // 8. Build booking payload according to PROMPT requirements
    const bookingPayload = {
      customer_id: user.id,
      category_id: categoryId, // REQUIRED
      city_id: cityId, // REQUIRED
      scheduled_at: scheduledAt, // REQUIRED
      photography_type: photoType,
      city: cityNameAr,
      region: bookingData.region || null, // SAFE MODE: Optional region for analytics/UX
      location: locationText,
      duration: (bookingData.duration || '2 ساعة').toString(),
      location_details: null, // Will be set below if needed
      final_price_sar: finalPrice, // REQUIRED - locked price
      price: finalPrice, // Keep for backward compatibility
      pricing_snapshot: pricingSnapshot, // REQUIRED - immutable snapshot
      status: 'pending_photographer_confirmation', // REQUIRED - as per PROMPT
      attempt_count: 1, // Default = 1
      hold_expires_at: holdExpiresAt, // 3 minutes from now
      booking_date: bookingData.date || bookingData.bookingDate || scheduledAt.split('T')[0],
      booking_time: bookingData.time || bookingData.bookingTime || '10:00',
      // Note: workload_bucket, workload_multiplier, and delivered_photos_bucket are stored in pricing_snapshot only, not as separate columns
      // Add shooting method field
      shooting_method: bookingData.shootingMethod || null,
      answers: buildBookingAnswers(bookingData),
    };

    // Add optional fields only if they exist in the schema
    // Note: precise_location and photographer_studio_location_snapshot columns may not exist
    // Store location data in location_details or pricing_snapshot instead
    
    // Build location_details with all location information
    const customerNote = bookingData.notes || bookingData.customerNotes || null;
    let locationDetails = bookingData.locationDetails || null;
    
    // SAFE MODE: Store precise location data in location_details as JSON object if available
    // CRITICAL: Only use final confirmed map selection, reject zero/default coordinates
    // This is critical for service radius validation in assign_next_photographer
    if (bookingData.preciseLocation && bookingData.preciseLocation.lat && bookingData.preciseLocation.lng) {
      const lat = parseFloat(bookingData.preciseLocation.lat);
      const lng = parseFloat(bookingData.preciseLocation.lng);
      
      // SAFE MODE: Validate coordinates - reject zero or default coordinates
      // Zero coordinates (0, 0) or very small values indicate invalid/unset location
      if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0 || Math.abs(lat) < 0.0001 || Math.abs(lng) < 0.0001) {
        console.error('❌ Invalid coordinates detected - rejecting zero/default coordinates:', {
          lat: bookingData.preciseLocation.lat,
          lng: bookingData.preciseLocation.lng,
          source: bookingData.preciseLocation.source
        });
        // Don't store invalid coordinates - location will be set from city center or other valid source
      } else {
        // Store in location_details as JSON string - only confirmed map selection
        // Ensure source is from map selection (pin) or confirmed GPS, not fallback
        const locationSource = bookingData.preciseLocation.source || 'manual';
        const isConfirmedLocation = locationSource === 'pin' || locationSource === 'gps' || locationSource === 'search';
        
        if (isConfirmedLocation) {
          const locationInfo = {
            type: 'precise',
            lat: lat,
            lng: lng,
            address_text: bookingData.preciseLocation.address_text || locationText,
            source: locationSource,
            city: bookingData.preciseLocation.city || bookingData.city || null,
            district: bookingData.preciseLocation.district || bookingData.district || null
          };
          locationDetails = JSON.stringify(locationInfo);
          // Store lat/lng in precise_location for assign_next_photographer (service radius)
          bookingPayload.precise_location = { lat, lng };
          console.log('✅ Storing confirmed location from map selection:', {
            lat,
            lng,
            source: locationSource,
            city: locationInfo.city,
            district: locationInfo.district
          });
        } else {
          console.warn('⚠️ Location source not confirmed, using city center fallback:', locationSource);
        }
      }
    }
    
    // Store studio location snapshot in location_details if available
    if (bookingData.shootingPlaceType === 'photographer_studio' && bookingData.photographerStudioLocationSnapshot) {
      const studioInfo = {
        type: 'studio',
        ...bookingData.photographerStudioLocationSnapshot
      };
      locationDetails = JSON.stringify(studioInfo);
    }
    
    // Set location_details in payload (customer note has no bookings.notes column in DB)
    if (customerNote && !locationDetails) {
      locationDetails = customerNote;
    } else if (customerNote && locationDetails) {
      try {
        const parsed = typeof locationDetails === 'string' ? JSON.parse(locationDetails) : locationDetails;
        locationDetails = JSON.stringify({ ...parsed, customer_note: customerNote });
      } catch {
        locationDetails = `${locationDetails}\n${customerNote}`;
      }
    }
    bookingPayload.location_details = locationDetails;
    
    // Store shooting place type in pricing_snapshot for reference
    if (bookingData.shootingPlaceType && bookingPayload.pricing_snapshot) {
      // pricing_snapshot is already an object, add shootingPlaceType to it
      bookingPayload.pricing_snapshot.shootingPlaceType = bookingData.shootingPlaceType;
    }

    // Validate critical fields before inserting
    console.log('📦 Booking payload validation:', {
      customer_id: bookingPayload.customer_id,
      category_id: bookingPayload.category_id,
      city_id: bookingPayload.city_id,
      scheduled_at: bookingPayload.scheduled_at,
      scheduled_at_valid: !isNaN(new Date(bookingPayload.scheduled_at).getTime()),
      final_price_sar: bookingPayload.final_price_sar,
      final_price_sar_valid: !isNaN(bookingPayload.final_price_sar) && bookingPayload.final_price_sar > 0,
      status: bookingPayload.status,
      hold_expires_at: bookingPayload.hold_expires_at,
      hold_expires_at_valid: !isNaN(new Date(bookingPayload.hold_expires_at).getTime()),
      pricing_snapshot_exists: !!bookingPayload.pricing_snapshot
    });
    
    // Final validation before insert
    if (!bookingPayload.category_id) {
      throw new Error('تعذر تحديد نوع التصوير. يرجى المحاولة مرة أخرى أو التواصل مع الدعم.');
    }
    if (!bookingPayload.city_id) {
      throw new Error('تعذر تحديد المدينة. يرجى اختيار مدينة من القائمة المتاحة.');
    }
    if (!bookingPayload.scheduled_at || isNaN(new Date(bookingPayload.scheduled_at).getTime())) {
      throw new Error('scheduled_at غير صالح. يرجى التحقق من التاريخ والوقت.');
    }
    if (!bookingPayload.final_price_sar || isNaN(bookingPayload.final_price_sar) || bookingPayload.final_price_sar <= 0) {
      throw new Error('final_price_sar غير صالح. يرجى التحقق من السعر.');
    }
    
    console.log('📦 Booking payload (final):', bookingPayload);

    // 9. Insert booking
    console.log('📝 Step 9: Inserting booking into database...');
    console.log('📝 Payload keys:', Object.keys(bookingPayload));
    
    const { data: booking, error } = await supabase
      .from('bookings')
      .insert(bookingPayload)
      .select()
      .single();

    console.log('📝 Insert result:', { 
      hasData: !!booking, 
      hasError: !!error,
      bookingId: booking?.id,
      errorCode: error?.code,
      errorMessage: error?.message
    });

    if (error) {
      console.error('❌ Error creating booking:', error);
      console.error('📋 Error details:', {
        code: error.code,
        message: error.message,
        hint: error.hint,
        details: error.details,
      });

      let errorMessage = 'فشل إنشاء الحجز. يرجى المحاولة مرة أخرى.';
      if (error.code === 'PGRST204' || error.message?.includes('Could not find')) {
        errorMessage =
          'خطأ في بيانات الحجز (عمود غير معروف في قاعدة البيانات). يرجى تحديث التطبيق أو التواصل مع الدعم.';
      } else if (error.code === '23503') {
        // Foreign key violation
        if (error.message?.includes('customer_id')) {
          errorMessage = 'خطأ في بيانات العميل. يرجى تسجيل الدخول مرة أخرى.';
        } else if (error.message?.includes('category_id')) {
          errorMessage = 'خطأ في نوع التصوير. يرجى المحاولة مرة أخرى.';
        } else if (error.message?.includes('city_id')) {
          errorMessage = 'خطأ في المدينة. يرجى المحاولة مرة أخرى.';
        } else {
          errorMessage = `خطأ في البيانات المرتبطة: ${error.message}`;
        }
      } else if (error.message?.includes('null value') || error.message?.includes('violates not-null')) {
        errorMessage = `خطأ في البيانات المطلوبة: ${error.message}. يرجى التحقق من جميع الحقول.`;
      } else if (error.message?.includes('column') || error.message?.includes('Could not find')) {
        errorMessage = `خطأ في قاعدة البيانات: ${error.message}. يرجى التحقق من migration.`;
      }
      
      throw new Error(errorMessage);
    }

    console.log('✅ Booking created successfully:', booking);

    // 10. Assign photographer
    // If assignedPhotographerId is provided in bookingData (direct booking), use it directly
    // Otherwise, use RPC to assign next available photographer
    let assignedPhotographerId = bookingData.assignedPhotographerId || null;
    
    try {
      if (assignedPhotographerId) {
        // Direct booking: photographer already assigned in payload
        console.log('📸 Direct booking: Using provided photographer ID:', assignedPhotographerId);
        
        // Update booking with assigned photographer
        const { error: updateError } = await supabase
          .from('bookings')
          .update({ 
            assigned_photographer_id: assignedPhotographerId,
            status: 'pending_photographer_confirmation',
            hold_expires_at: new Date(Date.now() + 3 * 60 * 1000).toISOString() // 3 minutes
          })
          .eq('id', booking.id);
        
        if (updateError) {
          console.error('❌ Failed to update booking with assigned photographer:', updateError);
          assignedPhotographerId = null;
        } else {
          console.log('✅ Booking updated with assigned photographer');
        }
      } else {
        // Normal booking: use RPC to assign next photographer
        console.log('🔄 Attempting to assign photographer for booking:', booking.id);
        const { data: rpcAssignedPhotographerId, error: assignError } = await supabase
          .rpc('assign_next_photographer', { p_booking_id: booking.id, p_max_attempts: 3 });

        if (assignError) {
          console.error('❌ Failed to assign photographer:', assignError);
          // Booking is created but no photographer assigned - will be handled by retry flow
          assignedPhotographerId = null;
        } else {
          assignedPhotographerId = rpcAssignedPhotographerId;
        }
      }

      if (assignedPhotographerId) {
        console.log('✅ Photographer assigned:', assignedPhotographerId);
        
        // Validate service radius if precise location is available
        if (bookingData.preciseLocation?.lat && bookingData.preciseLocation?.lng) {
          try {
            // Get photographer service area data
            const { data: photographerProfile, error: profileError } = await supabase
              .from('photographer_profiles')
              .select('service_center_lat, service_center_lng, service_radius_km')
              .eq('user_id', assignedPhotographerId)
              .single();

            if (!profileError && photographerProfile) {
              const serviceCenterLat = photographerProfile.service_center_lat;
              const serviceCenterLng = photographerProfile.service_center_lng;
              const serviceRadiusKm = photographerProfile.service_radius_km || 20;

              if (serviceCenterLat && serviceCenterLng) {
                const distanceKm = calculateDistance(
                  parseFloat(bookingData.preciseLocation.lat),
                  parseFloat(bookingData.preciseLocation.lng),
                  parseFloat(serviceCenterLat),
                  parseFloat(serviceCenterLng)
                );

                console.log('📏 Service radius check after assignment:', {
                  distanceKm: distanceKm.toFixed(2),
                  serviceRadiusKm: serviceRadiusKm,
                  withinRadius: distanceKm <= serviceRadiusKm
                });

                if (distanceKm > serviceRadiusKm) {
                  console.warn('⚠️ Customer location is outside photographer service radius');
                  // Update booking status to indicate issue
                  await supabase
                    .from('bookings')
                    .update({ 
                      status: 'pending_photographer_assignment',
                      assigned_photographer_id: null
                    })
                    .eq('id', booking.id);
                  
                  // Try to assign another photographer
                  const { data: nextPhotographerId } = await supabase
                    .rpc('assign_next_photographer', { p_booking_id: booking.id, p_max_attempts: 3 });
                  
                  if (nextPhotographerId) {
                    console.log('✅ Assigned alternative photographer:', nextPhotographerId);
                    booking.assigned_photographer_id = nextPhotographerId;
                    booking.assignedPhotographerId = nextPhotographerId;
                  } else {
                    console.warn('⚠️ No alternative photographer found within service radius');
                    booking.assigned_photographer_id = null;
                    booking.assignedPhotographerId = null;
                  }
                }
              }
            }
          } catch (radiusCheckError) {
            console.warn('⚠️ Failed to check service radius:', radiusCheckError);
            // Continue - don't block booking creation
          }
        }
        
        // Update booking object with assigned photographer ID
        booking.assigned_photographer_id = assignedPhotographerId;
        booking.assignedPhotographerId = assignedPhotographerId;

        // Push + WhatsApp + SMS (email via trg_send_booking_emails on INSERT)
        await notifyPhotographerNewBookingChannels(
          assignedPhotographerId,
          booking.id,
          booking,
          { includeEmail: false },
        );

        // Booking emails handled server-side by trg_send_booking_emails DB trigger
      } else {
        console.warn('⚠️ No photographer assigned - no available photographers found');
        console.warn('Booking details:', {
          city: booking.city || bookingData.city,
          photographyType: booking.photography_type || bookingData.photographyType,
          status: booking.status
        });
      }
    } catch (rpcError) {
      console.error('❌ RPC function error:', rpcError);
      // Continue - booking is created, photographer can be assigned manually or via retry
    }

    return toCamelCase(booking);
  },

  // ─── إنهاء الجلسة: تحديث الحالة إلى awaiting_delivery ──────────────────

  /**
   * المصور ينهي الجلسة → الحالة تصبح awaiting_delivery
   */
  async completeSession(bookingId) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Not authenticated');

    const endedAt = new Date().toISOString();
    const { data: rows, error } = await supabase
      .from('bookings')
      .update({
        status: 'awaiting_delivery',
        session_ended_at: endedAt,
        updated_at: endedAt,
      })
      .eq('id', bookingId)
      .select('id');

    if (error) throw new Error(error.message || 'فشل تحديث الحالة');

    if (!rows || rows.length === 0) {
      throw new Error('لا صلاحية لتعديل هذا الحجز — تأكد من تطبيق إصلاح قاعدة البيانات من لوحة Supabase');
    }

    return { success: true };
  },

  // ─── نظام PIN التحقق من هوية المصور ──────────────────────────────────────

  /**
   * المصور يبدأ الجلسة: يُولَّد PIN عشوائي ويُرسَل للعميل.
   * لا تتغير الحالة هنا — تتغير بعد إدخال PIN الصحيح.
   * @returns {{ pin: string, customerId: string }}
   */
  /**
   * @param {string} bookingId
   * @param {string} customerId - ممرَّر من الـ component (لا نحتاج fetch من DB)
   * @param {number|null} gpsLat
   * @param {number|null} gpsLng
   */
  async initiateSession(bookingId, customerId, gpsLat = null, gpsLng = null) {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('Not authenticated');
    if (!customerId) throw new Error('بيانات الحجز غير مكتملة');

    // توليد PIN جديد (4 أرقام)
    const pin = String(Math.floor(1000 + Math.random() * 9000));

    // نحدّث الحجز — RLS تسمح للمصور عبر assigned_photographer_id أو photographer_id
    const { data: updatedRows, error: updateErr } = await supabase
      .from('bookings')
      .update({
        session_pin: pin,
        session_start_lat: gpsLat,
        session_start_lng: gpsLng,
        updated_at: new Date().toISOString(),
      })
      .eq('id', bookingId)
      .select('id');

    if (updateErr) throw updateErr;

    // إذا رجع 0 صفوف، يعني RLS حجبت الكتابة (لا خطأ يُرمى لكن لا شيء كُتب)
    if (!updatedRows || updatedRows.length === 0) {
      throw new Error('فشل توليد رمز الجلسة — تأكد أن الحجز مخصص لك. حاول تحديث الصفحة.');
    }

    // الإشعار الداخلي يُرسَل تلقائياً عبر DB trigger (notify_customer_on_session_pin)
    // FCM Push: يُرسَل هنا لإيصال الرمز للعميل حتى لو التطبيق مغلق
    try {
      const { sendNotification } = await import('./notificationService');
      sendNotification({
        userId: customerId,
        type: 'booking',
        title: '📸 المصور وصل! رمز جلستك: ' + pin,
        message: 'أعطِ هذا الرمز للمصور لبدء الجلسة رسمياً. الرمز: ' + pin,
        bookingId,
        fcmData: { type: 'SESSION_PIN', pin },
        skipInApp: true, // الإشعار الداخلي تكفّل به الـ DB Trigger
      });
    } catch (_) {}

    return { pin, customerId };
  },

  /**
   * المصور يُدخل الـ PIN الذي أعطاه العميل.
   * عند التطابق → الجلسة تبدأ رسمياً.
   */
  /**
   * @param {string} bookingId
   * @param {string} enteredPin - الرمز الذي أدخله المصور
   * @param {string} customerId - ممرَّر من الـ component
   * @param {string} generatedPin - الـ PIN المحفوظ في state الـ component
   */
  async verifySessionPin(bookingId, enteredPin, customerId, generatedPin) {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('Not authenticated');

    const { data: row, error: fetchErr } = await supabase
      .from('bookings')
      .select('session_pin, status')
      .eq('id', bookingId)
      .maybeSingle();

    if (fetchErr) throw new Error(fetchErr.message || 'فشل التحقق من الرمز');
    if (!row) throw new Error('لم يتم العثور على الحجز');
    if (row.status !== 'paid_confirmed') {
      throw new Error('لا يمكن تأكيد الرمز في الحالة الحالية');
    }

    const storedPin = row.session_pin || generatedPin;
    if (!storedPin) throw new Error('لم يتم توليد رمز بعد، ابدأ الجلسة أولاً');
    if (String(enteredPin).trim() !== String(storedPin).trim()) {
      throw new Error('الرمز غير صحيح — اطلب من العميل إعادة عرض الرمز');
    }

    // الرمز صحيح → غيّر الحالة وامسح الـ PIN
    const { data: verifyRows, error: updateErr } = await supabase
      .from('bookings')
      .update({
        status: 'session_started',
        session_started_at: new Date().toISOString(),
        session_pin: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', bookingId)
      .select('id');

    if (updateErr) throw updateErr;

    if (!verifyRows || verifyRows.length === 0) {
      throw new Error('فشل تأكيد بدء الجلسة — تأكد أن الحجز مخصص لك. حاول تحديث الصفحة.');
    }

    // 🔔 إشعار للعميل: الجلسة بدأت رسمياً ✅
    if (customerId) {
      try {
        const { sendNotification } = await import('./notificationService');
        sendNotification({
          userId: customerId,
          type: 'booking',
          title: '🎬 جلستك بدأت رسمياً!',
          message: 'تم التحقق من هوية المصور. جلستك جارية الآن.',
          bookingId,
          fcmData: { type: 'SESSION_STARTED' },
        });
      } catch (_) {}
    }

    return true;
  },

  // Update booking status
  async updateBookingStatus(bookingId, status) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('bookings')?.update({ status, updated_at: new Date()?.toISOString() })?.eq('id', bookingId)?.select()?.single();

    if (error) throw error;

    // 🔔 إشعارات موحدة (FCM + in-app) بناءً على الحالة الجديدة
    try {
      const {
        notifyCustomerBookingExpired,
        notifyPhotographerBookingExpired,
        notifyPhotographerPaymentConfirmed,
        notifyPhotographerCustomerCancelled,
        notifyCustomerAdminCancelled,
        notifyPhotographerAdminCancelled,
      } = await import('./notificationService');

      const photographerId = data?.photographer_id || data?.assigned_photographer_id;

      if (status === 'expired') {
        if (data?.customer_id) notifyCustomerBookingExpired(data.customer_id, bookingId);
        if (photographerId) notifyPhotographerBookingExpired(photographerId, bookingId);
      } else if (status === 'paid_confirmed' && photographerId) {
        notifyPhotographerPaymentConfirmed(photographerId, bookingId);
        // SMS للعميل — تم تأكيد الحجز والدفع
        try {
          const custId = data?.customer_id;
          if (custId) {
            const { data: cust } = await supabase
              .from('user_profiles')
              .select('phone')
              .eq('id', custId)
              .maybeSingle();
            const { data: phUser } = await supabase
              .from('user_profiles')
              .select('full_name')
              .eq('id', photographerId)
              .maybeSingle();
            if (cust?.phone) {
              const sched = data?.scheduled_at ? new Date(data.scheduled_at) : null;
              const dateStr = sched
                ? sched.toLocaleDateString('ar-SA-u-ca-gregory', { weekday: 'long', day: 'numeric', month: 'long' })
                : '—';
              const timeStr = sched
                ? sched.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })
                : '';
              const shortRef = String(bookingId).slice(0, 8).toUpperCase();
              await sendSmsNonBlocking({
                phone: cust.phone,
                message: `تم تأكيد حجزك رقم ${shortRef} يوم: ${dateStr} ، الساعة: ${timeStr} . نتمنى لك جلسة ممتعة!`,
              });
            }
          }
        } catch (smsPc) {
          console.warn('SMS customer paid_confirmed failed (non-blocking):', smsPc?.message || smsPc);
        }
      } else if (status === 'cancelled_by_customer' && photographerId) {
        notifyPhotographerCustomerCancelled(photographerId, bookingId);
      } else if (status === 'cancelled_by_admin') {
        if (data?.customer_id) notifyCustomerAdminCancelled(data.customer_id, bookingId);
        if (photographerId) notifyPhotographerAdminCancelled(photographerId, bookingId);
      }
    } catch (e) {
      console.warn('Notification trigger failed (non-blocking):', e);
    }

    // Booking emails handled server-side by trg_send_booking_emails DB trigger

    return toCamelCase(data);
  },

  // Get single booking
  async getBooking(bookingId) {
    const { data: booking, error } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .single();

    if (error) throw error;
    if (!booking) return null;

    // Get related user profiles
    const userIds = [booking.customer_id, booking.photographer_id, booking.assigned_photographer_id].filter(Boolean);
    let userProfiles = {};
    let photographerProfiles = {};
    
    if (userIds.length > 0) {
      const [profilesResult, photographerProfilesResult] = await Promise.all([
        // Get user profiles
        supabase
          .from('user_profiles')
          .select('id, full_name, email, phone, avatar_url')
          .in('id', userIds),
        
        // Get photographer profiles (avatar_url + other fields for display)
        booking.photographer_id || booking.assigned_photographer_id
          ? supabase
              .from('photographer_profiles')
              .select('user_id, avatar_url, rating, total_bookings, experience_level, city, specializations')
              .in('user_id', [booking.photographer_id, booking.assigned_photographer_id].filter(Boolean))
          : Promise.resolve({ data: null, error: null })
      ]);

      if (!profilesResult.error && profilesResult.data) {
        profilesResult.data.forEach(profile => {
          userProfiles[profile.id] = profile;
        });
      }

      if (!photographerProfilesResult.error && photographerProfilesResult.data) {
        photographerProfilesResult.data.forEach(profile => {
          photographerProfiles[profile.user_id] = profile;
        });
      }
    }

    // تحديد المصور للعرض: photographer_id إن وُجد (بعد القبول)، وإلا assigned_photographer_id (حجز عادي قبل القبول)
    const photographerId = booking.photographer_id || booking.assigned_photographer_id;
    const photographerProfile = photographerProfiles[photographerId] || null;
    
    // SAFE MODE: Build photographer object for display (includes both user profile and photographer profile data)
    const photographerUserProfile = userProfiles[photographerId] || null;
    const assignedPhotographerUserProfile = userProfiles[booking.assigned_photographer_id] || null;
    
    // Build comprehensive photographer object (avatar from user_profiles or photographer_profiles)
    const photographerData = photographerUserProfile ? {
      ...photographerUserProfile,
      ...(photographerProfile || {}),
      fullName: photographerUserProfile.full_name,
      avatar_url: photographerUserProfile.avatar_url || photographerProfile?.avatar_url,
      avatarUrl: photographerUserProfile.avatar_url || photographerProfile?.avatar_url
    } : null;

    const assignedProfile = photographerProfiles[booking.assigned_photographer_id];
    const assignedPhotographerData = assignedPhotographerUserProfile ? {
      ...assignedPhotographerUserProfile,
      ...(assignedProfile || {}),
      fullName: assignedPhotographerUserProfile.full_name,
      avatar_url: assignedPhotographerUserProfile.avatar_url || assignedProfile?.avatar_url,
      avatarUrl: assignedPhotographerUserProfile.avatar_url || assignedProfile?.avatar_url
    } : null;

    // Combine booking with user profiles and photographer profile
    const enrichedBooking = {
      ...booking,
      customer: userProfiles[booking.customer_id] || null,
      photographer: photographerData,
      photographerProfile: photographerProfile,
      // SAFE MODE: Add assignedPhotographer for direct bookings and normal bookings
      assignedPhotographer: assignedPhotographerData || photographerData
    };

    return toCamelCase(enrichedBooking);
  },

  // Photographer: Accept booking (confirm availability)
  async acceptBooking(bookingId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Verify photographer is assigned to this booking
    const { data: booking, error: fetchError } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .eq('assigned_photographer_id', user.id)
      .eq('status', 'pending_photographer_confirmation')
      .single();

    if (fetchError || !booking) {
      throw new Error('Booking not found or not assigned to you');
    }

    // Check if hold has expired
    if (booking.hold_expires_at) {
      const holdExpiresAt = new Date(booking.hold_expires_at);
      const now = new Date();
      
      if (holdExpiresAt < now) {
        // Hold has expired, try to assign next photographer or mark as expired
        throw new Error('انتهت مدة الانتظار. لا يمكن قبول الحجز الآن.');
      }
    }

    // Update booking status
    const { data, error } = await supabase
      .from('bookings')
      .update({
        status: 'awaiting_payment',
        photographer_id: user.id,
        photographer_response_at: new Date().toISOString(),
        payment_expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString(), // 60 minutes
        updated_at: new Date().toISOString()
      })
      .eq('id', bookingId)
      .select()
      .single();

    if (error) throw error;

    // 🔔 إشعار للعميل: المصور وافق — أكمل الدفع
    try {
      const { notifyCustomerPhotographerAccepted } = await import('./notificationService');
      notifyCustomerPhotographerAccepted(booking.customer_id, bookingId);
    } catch (_) {}

    // Booking emails handled server-side by trg_send_booking_emails DB trigger

    return toCamelCase(data);
  },

  // Photographer: Decline booking
  async declineBooking(bookingId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Verify photographer is assigned
    const { data: booking, error: fetchError } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .eq('assigned_photographer_id', user.id)
      .eq('status', 'pending_photographer_confirmation')
      .single();

    if (fetchError || !booking) {
      throw new Error('Booking not found or not assigned to you');
    }

    // Mark as declined and assign next photographer
    const { data: nextPhotographer, error: assignError } = await supabase
      .rpc('assign_next_photographer', { p_booking_id: bookingId, p_max_attempts: 3 });

    if (assignError) {
      console.error('Failed to assign next photographer:', assignError);
    }

    // Update booking with decline response
    const { data, error } = await supabase
      .from('bookings')
      .update({
        photographer_response_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', bookingId)
      .select()
      .single();

    if (error) throw error;

    if (nextPhotographer) {
      await notifyPhotographerNewBookingChannels(nextPhotographer, bookingId, data, {
        includeEmail: true,
      });
      try {
        const { notifyCustomerPhotographerDeclined } = await import('./notificationService');
        if (data?.customer_id) {
          notifyCustomerPhotographerDeclined(data.customer_id, bookingId);
        }
      } catch (e) {
        console.warn('FCM decline-reassign push failed (non-blocking):', e);
      }
    }

    return toCamelCase(data);
  },

  // Customer: Retry booking (find next photographer)
  async retryBooking(bookingId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Verify customer owns this booking
    const { data: booking, error: fetchError } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .eq('customer_id', user.id)
      .in('status', ['declined', 'expired', 'pending_photographer_confirmation'])
      .single();

    if (fetchError || !booking) {
      throw new Error('Booking not found or cannot be retried');
    }

    // Assign next photographer
    const { data: nextPhotographer, error: assignError } = await supabase
      .rpc('assign_next_photographer', { p_booking_id: bookingId, p_max_attempts: 3 });

    if (assignError) {
      throw assignError;
    }

    // Get updated booking
    const { data, error } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .single();

    if (error) throw error;

    if (nextPhotographer) {
      await notifyPhotographerNewBookingChannels(nextPhotographer, bookingId, data, {
        includeEmail: true,
      });
    }

    return toCamelCase(data);
  },

  // Customer: Select a specific photographer directly from the alternatives panel
  async retryBookingWithPhotographer(bookingId, photographerId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Verify customer owns this booking and it can be reassigned
    const { data: booking, error: fetchError } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .eq('customer_id', user.id)
      .in('status', ['declined', 'expired', 'pending_photographer_confirmation'])
      .single();

    if (fetchError || !booking) {
      throw new Error('Booking not found or cannot be reassigned');
    }

    const newAttemptCount = (booking.attempt_count || 0) + 1;
    const holdExpiresAt = new Date(Date.now() + 3 * 60 * 1000).toISOString();

    // Directly assign the chosen photographer
    const { data: updated, error: updateError } = await supabase
      .from('bookings')
      .update({
        assigned_photographer_id: photographerId,
        status: 'pending_photographer_confirmation',
        hold_expires_at: holdExpiresAt,
        attempt_count: newAttemptCount,
        updated_at: new Date().toISOString(),
      })
      .eq('id', bookingId)
      .select()
      .single();

    if (updateError) throw updateError;

    await notifyPhotographerNewBookingChannels(photographerId, bookingId, updated, {
      includeEmail: true,
    });

    return toCamelCase(updated);
  },

  // Get booking by ID (for waiting/payment pages)
  async getBookingById(bookingId) {
    return this.getBooking(bookingId);
  },

  // ─── Cancellation after payment ───────────────────────────────────────────

  /**
   * Calculate cancellation fee percentage for a customer request.
   * Photographer cancellations are always 0%.
   * @param {string} bookingDate  e.g. "2026-04-10"
   * @param {string} bookingTime  e.g. "14:00"
   * @param {string} requestedBy  'customer' | 'photographer'
   * @returns {number} fee percentage (0 | 10 | 30 | 50)
   */
  calculateCancellationFee(bookingDate, bookingTime, requestedBy) {
    if (requestedBy === 'photographer') return 0;
    if (!bookingDate) return 50;

    const timeStr = bookingTime || '00:00';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const sessionDate = new Date(bookingDate);
    sessionDate.setHours(hours || 0, minutes || 0, 0, 0);

    const hoursUntil = (sessionDate.getTime() - Date.now()) / 3_600_000;

    if (hoursUntil >= 48) return 10;
    if (hoursUntil >= 24) return 30;
    return 50;
  },

  /**
   * Submit a cancellation request for a paid_confirmed booking.
   *
   * Photographer → immediate cancellation (cancelled_by_photographer), no admin approval needed.
   * Customer     → goes to cancellation_requested for admin review (manual refund).
   */
  async requestCancellation(bookingId, requestedBy, reason = '') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Fetch booking — filter by caller identity so RLS allows access
    const { data: booking, error: fetchErr } = await supabase
      .from('bookings')
      .select('status, booking_date, booking_time, customer_id, assigned_photographer_id, final_price_sar, price')
      .eq('id', bookingId)
      .or(`customer_id.eq.${user.id},assigned_photographer_id.eq.${user.id}`)
      .single();

    if (fetchErr || !booking) throw new Error('لم يتم العثور على الحجز');
    if (booking.status !== 'paid_confirmed')
      throw new Error('لا يمكن طلب الإلغاء إلا للحجوزات المؤكدة');

    const feePct = this.calculateCancellationFee(
      booking.booking_date,
      booking.booking_time,
      requestedBy
    );

    // Fetch names for admin email (non-blocking, best effort)
    const bookingShortId = String(bookingId).slice(0, 8).toUpperCase();
    const fetchNames = async () => {
      const ids = [booking.customer_id, booking.assigned_photographer_id].filter(Boolean);
      if (!ids.length) return { customerName: '', photographerName: '' };
      const { data: profiles } = await supabase
        .from('user_profiles').select('id, full_name').in('id', ids);
      const byId = Object.fromEntries((profiles || []).map(p => [p.id, p.full_name || '']));
      return {
        customerName: byId[booking.customer_id] || '',
        photographerName: byId[booking.assigned_photographer_id] || '',
      };
    };

    // ── Photographer withdrawal: immediate, no admin approval ──────────────
    if (requestedBy === 'photographer') {
      const { error: updateErr } = await supabase
        .from('bookings')
        .update({
          status: 'cancelled_by_photographer',
          cancellation_requested_by: 'photographer',
          cancellation_requested_at: new Date().toISOString(),
          cancellation_fee_pct: 0,
          cancellation_reason: reason || null,
          updated_at: new Date().toISOString(),
          // payment_status stays as-is — refund only queued when customer explicitly requests it
        })
        .eq('id', bookingId);

      if (updateErr) throw new Error(updateErr.message);

      // Apply withdrawal penalty
      const penalty = await this._applyWithdrawalPenalty(booking.assigned_photographer_id || user.id);

      // Notify customer immediately (push + SMS)
      try {
        const { notifyPhotographerWithdrew } = await import('./notificationService');
        if (booking.customer_id) notifyPhotographerWithdrew(booking.customer_id, bookingId);
      } catch (e) {
        console.warn('Photographer withdrawal notification failed (non-blocking):', e);
      }

      // Send SMS to customer
      try {
        const { data: customerProfile } = await supabase
          .from('user_profiles')
          .select('phone')
          .eq('id', booking.customer_id)
          .single();
        const customerPhone = customerProfile?.phone;
        if (customerPhone) {
          const smsMessage = 'عميلنا الغالي، اعتذر المصور عن الحضور. يمكنك اختيار مصور آخر أو استرداد كامل المبلغ من قسم (حجوزاتي) داخل التطبيق.';
          supabase.functions.invoke('send-sms', {
            body: { phone: customerPhone, message: smsMessage },
          }).catch(e => console.warn('SMS send failed (non-blocking):', e));
        }
      } catch (e) {
        console.warn('SMS notification failed (non-blocking):', e);
      }

      // Notify admin via email
      fetchNames().then(({ customerName, photographerName }) => {
        sendAdminEmail({
          templateKey: 'admin_photographer_withdrawal',
          templateData: {
            booking_id: bookingShortId,
            photographer_name: photographerName,
            customer_name: customerName,
            withdrawal_reason: reason || 'لم يُحدد',
            withdrawal_count: String(penalty?.newCount || ''),
          },
        });
      }).catch(() => {});

      return { feePct: 0, refundPct: 100, immediate: true };
    }

    // ── Customer cancellation: goes to admin for review ────────────────────
    const { error: updateErr } = await supabase
      .from('bookings')
      .update({
        status: 'cancellation_requested',
        cancellation_requested_by: requestedBy,
        cancellation_requested_at: new Date().toISOString(),
        cancellation_fee_pct: feePct,
        cancellation_reason: reason || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', bookingId);

    if (updateErr) throw new Error(updateErr.message);

    // Notify admin via email
    fetchNames().then(({ customerName, photographerName }) => {
      sendAdminEmail({
        templateKey: 'admin_customer_cancellation',
        templateData: {
          booking_id: bookingShortId,
          customer_name: customerName,
          photographer_name: photographerName,
          cancellation_reason: reason || 'لم يُحدد',
          fee_pct: String(feePct),
          refund_pct: String(100 - feePct),
        },
      });
    }).catch(() => {});

    return { feePct, refundPct: 100 - feePct, immediate: false };
  },

  /**
   * Admin: approve or reject a cancellation request.
   * approve=true  → status becomes cancelled_by_customer / cancelled_by_photographer
   * approve=false → status reverts to paid_confirmed
   */
  async resolveCancellationRequest(bookingId, approve) {
    const { data: booking, error: fetchErr } = await supabase
      .from('bookings')
      .select('status, cancellation_requested_by, cancellation_fee_pct, customer_id, assigned_photographer_id')
      .eq('id', bookingId)
      .single();

    if (fetchErr || !booking) throw new Error('لم يتم العثور على الحجز');
    if (booking.status !== 'cancellation_requested')
      throw new Error('الحجز ليس في حالة انتظار الإلغاء');

    let newStatus;
    if (approve) {
      newStatus = booking.cancellation_requested_by === 'photographer'
        ? 'cancelled_by_photographer'
        : 'cancelled_by_customer';
    } else {
      newStatus = 'paid_confirmed';
    }

    const updatePayload = {
      status: newStatus,
      updated_at: new Date().toISOString(),
    };

    // Clear request fields on rejection
    if (!approve) {
      updatePayload.cancellation_requested_by = null;
      updatePayload.cancellation_requested_at = null;
      updatePayload.cancellation_fee_pct = 0;
      updatePayload.cancellation_reason = null;
    } else if (approve) {
      // payment_status is set by process-refund edge function after Moyasar confirms
    }

    const { error: updateErr } = await supabase
      .from('bookings')
      .update(updatePayload)
      .eq('id', bookingId);

    if (updateErr) throw new Error(updateErr.message);

    // ── Photographer withdrawal penalty ───────────────────────────────────
    let penaltyApplied = null;
    if (approve && booking.cancellation_requested_by === 'photographer' && booking.assigned_photographer_id) {
      penaltyApplied = await this._applyWithdrawalPenalty(booking.assigned_photographer_id);
    }

    // Notify both parties
    try {
      const { notifyCancellationDecision } = await import('./notificationService');
      notifyCancellationDecision(
        booking.customer_id,
        booking.assigned_photographer_id,
        approve,
        booking.cancellation_fee_pct,
        bookingId
      );
    } catch (e) {
      console.warn('Cancellation decision notification failed (non-blocking):', e);
    }

    return { newStatus, penaltyApplied };
  },

  // ─── Customer refund request ──────────────────────────────────────────────

  /**
   * Called when a customer presses "استرداد المبلغ" after a photographer withdrew.
   * Sets payment_status = 'refund_pending' so the admin sees it in the refund queue.
   */
  async requestRefund(bookingId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: booking, error: fetchErr } = await supabase
      .from('bookings')
      .select('status, customer_id, assigned_photographer_id, payment_status')
      .eq('id', bookingId)
      .or(`customer_id.eq.${user.id},assigned_photographer_id.eq.${user.id}`)
      .single();

    if (fetchErr || !booking) throw new Error('لم يتم العثور على الحجز');
    if (booking.status !== 'cancelled_by_photographer')
      throw new Error('لا يمكن طلب الاسترداد إلا بعد انسحاب المصور');
    if (booking.payment_status === 'refund_pending')
      throw new Error('تم تقديم طلب الاسترداد مسبقاً');

    const { error } = await supabase
      .from('bookings')
      .update({
        payment_status: 'refund_pending',
        updated_at: new Date().toISOString(),
      })
      .eq('id', bookingId);

    if (error) throw new Error(error.message);
    return true;
  },

  // ─── Withdrawal penalty engine ────────────────────────────────────────────

  /**
   * Increment withdrawal_count for a photographer and apply tiered penalties:
   *   Count 1 → warning only
   *   Count 2 → 7-day visibility penalty (hidden from search)
   *   Count 3+ → 14-day suspension from new bookings
   *
   * Returns { newCount, penalty: 'warning' | 'visibility' | 'suspension' }
   */
  async _applyWithdrawalPenalty(photographerId) {
    const { data: profile, error } = await supabase
      .from('photographer_profiles')
      .select('withdrawal_count')
      .eq('user_id', photographerId)
      .single();

    if (error || !profile) return null;

    const newCount = (profile.withdrawal_count || 0) + 1;
    const now = new Date();

    const patch = { withdrawal_count: newCount };

    let penalty = 'warning';
    if (newCount >= 3) {
      penalty = 'suspension';
      const until = new Date(now);
      until.setDate(until.getDate() + 14);
      // إخفاء من البحث + حظر من قبول الحجوزات لمدة 14 يوماً
      patch.withdrawal_suspended_until = until.toISOString();
      patch.visibility_penalty_until = until.toISOString();
    } else if (newCount === 2) {
      penalty = 'visibility';
      const until = new Date(now);
      until.setDate(until.getDate() + 7);
      patch.visibility_penalty_until = until.toISOString();
    }

    await supabase
      .from('photographer_profiles')
      .update(patch)
      .eq('user_id', photographerId);

    return { newCount, penalty };
  },

  /**
   * Check if a photographer is currently suspended from new bookings.
   * Returns { suspended: boolean, suspendedUntil: Date|null }
   */
  async checkPhotographerSuspension(photographerId) {
    const { data, error } = await supabase
      .from('photographer_profiles')
      .select('withdrawal_suspended_until, withdrawal_count')
      .eq('user_id', photographerId)
      .single();

    if (error || !data) return { suspended: false, suspendedUntil: null, withdrawalCount: 0 };

    const until = data.withdrawal_suspended_until
      ? new Date(data.withdrawal_suspended_until)
      : null;
    const suspended = until && until > new Date();
    return {
      suspended: !!suspended,
      suspendedUntil: suspended ? until : null,
      withdrawalCount: data.withdrawal_count ?? 0,
    };
  },
};